/**
 * How to play (docs/spec/07 §2): a page per idea, each with the real sprites acting it out and a
 * sentence or two. Left/right turn the pages; the words type themselves out.
 */

import { atlasFor } from '../../render/atlas';
import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput } from '../scene';
import { UiScene } from '../kit/base';
import { drawPanel } from '../kit/chrome';
import type { Rect } from '../kit/menuModel';
import { clampIndex, inRect } from '../kit/menuModel';
import { drawSpinner } from '../kit/spinner';
import { wrap } from '../kit/text';
import { drawUncover } from '../kit/uncover';
import { animFrame, blink, CAVE_A, FAINT, INK, MUTED, SELECTED } from '../kit/theme';
import type { Stage } from './helpStages';
import { boulderStage, castStage, creatureStage, goalStage, STAGE_COLS, STAGE_ROWS } from './helpStages';
import { amoebaStage, controlsStage, deepStage, scoreStage } from './helpStages2';
import { PAGES } from './helpPages';

/** Remembered for the session. */
let lastPage = 0;

const PANEL: Rect = { x: 36, y: 31, w: 248, h: 86 };
const STAGE: Rect = { x: 40, y: 34, w: STAGE_COLS * 16, h: STAGE_ROWS * 16 };
const PREV: Rect = { x: 0, y: 28, w: 34, h: 96 };
const NEXT: Rect = { x: 286, y: 28, w: 34, h: 96 };
const TEXT_Y = 124;
const TYPE_MS = 12;

export class HelpScene extends UiScene {
  protected readonly heading = 'HOW TO PLAY';
  private page = lastPage;
  private since = 0;

  protected override describe(): string {
    const p = PAGES[this.page];
    return p ? `How to play, page ${this.page + 1} of ${PAGES.length}: ${p.title}. ${p.text}` : 'How to play';
  }

  protected override hint(): string {
    return `◄► PAGE   ${this.words().back} BACK`;
  }

  protected override tick(dt: number): void {
    this.since += dt;
  }

  private stage(): Stage {
    const t = this.since;
    const anim = animFrame(t);
    switch (this.page) {
      case 0: return goalStage(t, anim);
      case 1: return castStage(t, anim);
      case 2: return boulderStage(t, anim);
      case 3: return creatureStage(t, anim);
      case 4: return amoebaStage(t, anim);
      case 5: return deepStage(t, anim);
      case 6: return scoreStage(t, anim);
      default: return controlsStage(this.keyText());
    }
  }

  private keyText(): { move: string; move2: string; grab: string; pause: string; pad: string } {
    const k = this.services.options.get().keys;
    const name = (code: string): string => (code ? this.services.input.keyName(code) : '');
    const join = (...codes: string[]): string => codes.map(name).filter((s) => s && s !== '---').join(' ');
    return {
      move: join(k.up[0], k.down[0], k.left[0], k.right[0]),
      move2: join(k.up[1], k.down[1], k.left[1], k.right[1]),
      grab: `${join(k.fire[0], k.fire[1])} SHIFT CTRL`.trim(),
      pause: join(k.pause[0], k.pause[1]),
      pad: 'STICK, A, START',
    };
  }

  protected paint(screen: Screen): void {
    const p = PAGES[this.page];
    if (!p) return;
    screen.text(p.title, 160, 20, SELECTED, 'center');
    drawPanel(screen, PANEL);
    const ctx = screen.ctx;
    const atlas = atlasFor(CAVE_A);
    const stage = this.stage();
    ctx.save();
    ctx.beginPath();
    ctx.rect(STAGE.x, STAGE.y, STAGE.w, STAGE.h);
    ctx.clip();
    for (const a of stage.actors) atlas.draw(ctx, a.sprite, a.frame, STAGE.x + a.x, STAGE.y + a.y);
    for (const l of stage.labels) {
      screen.text(l.text, STAGE.x + l.x, STAGE.y + l.y, l.colour, l.left === true ? 'left' : 'center');
    }
    ctx.restore();
    drawUncover(screen, STAGE, this.since, this.page + 1, CAVE_A);
    const shown = Math.floor(this.since / TYPE_MS);
    let left = shown;
    wrap(p.text, 38).slice(0, 4).forEach((line, i) => {
      const part = line.slice(0, Math.max(0, left));
      left -= line.length + 1;
      screen.text(part, 160 - (line.length * 8) / 2, TEXT_Y + i * 10, INK);
    });
    this.paintArrows(screen);
    this.paintDots(screen);
  }

  private paintArrows(screen: Screen): void {
    const on = blink(this.ms, 900);
    if (this.page > 0) screen.text('◄', 14, 70, on ? SELECTED : MUTED);
    if (this.page < PAGES.length - 1) screen.text('►', 298, 70, on ? SELECTED : MUTED);
  }

  private paintDots(screen: Screen): void {
    const x0 = 160 - (PAGES.length * 12) / 2;
    PAGES.forEach((_, i) => {
      if (i === this.page) drawSpinner(screen, x0 + i * 12 + 2, 172, this.ms);
      else screen.text('·', x0 + i * 12 + 2, 172, FAINT);
    });
  }

  private turn(delta: number): void {
    const next = clampIndex(this.page + delta, PAGES.length);
    if (next === this.page) return this.sound('error');
    this.page = next;
    lastPage = next;
    this.since = 0;
    this.sound('move');
    this.say(this.describe());
  }

  protected onAction(a: MenuAction): void {
    if (a === 'left' || a === 'up') this.turn(-1);
    else if (a === 'right' || a === 'down') this.turn(1);
    else if (a === 'confirm') {
      if (this.page < PAGES.length - 1) this.turn(1);
      else this.back();
    }
  }

  protected override onPointer(p: PointerInput): void {
    if (p.kind === 'wheel') return this.turn(p.delta);
    if (p.kind !== 'down' || p.button !== 0) return;
    if (inRect(PREV, p.x, p.y)) this.turn(-1);
    else if (inRect(NEXT, p.x, p.y)) this.turn(1);
    else if (p.y >= 166 && p.y < 182) {
      const x0 = 160 - (PAGES.length * 12) / 2;
      const i = Math.floor((p.x - x0) / 12);
      if (i >= 0 && i < PAGES.length && i !== this.page) this.turn(i - this.page);
    }
  }
}
